import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { X, Phone, Tag, Mail, Loader2, CheckCircle } from "lucide-react";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "overhaul_promo_dismissed_at";

export default function PromoPopup() {
  const [config, setConfig] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [showTab, setShowTab] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", sms_consent: false });
  const { formatted_phone, phone_link } = useSiteConfig();
  const location = useLocation();

  const isAdmin = location.pathname.startsWith("/admin");

  useEffect(() => {
    if (isAdmin) return;
    axios.get(`${API}/cms/promo-popup`)
      .then(res => {
        if (res.data?.enabled) {
          setConfig(res.data);
          setShowTab(true);
          schedulePopup(res.data);
        }
      })
      .catch(() => {});
  }, [isAdmin]);

  const schedulePopup = useCallback((cfg) => {
    const dismissedAt = localStorage.getItem(STORAGE_KEY);
    if (dismissedAt) {
      const elapsed = Date.now() - parseInt(dismissedAt, 10);
      const cooldownMs = (cfg.cooldown_hours || 24) * 60 * 60 * 1000;
      if (elapsed < cooldownMs) return;
    }
    const delay = (cfg.delay_seconds || 10) * 1000;
    const timer = setTimeout(() => setShowPopup(true), delay);
    return () => clearTimeout(timer);
  }, []);

  const dismiss = () => {
    setShowPopup(false);
    setSubmitted(false);
    localStorage.setItem(STORAGE_KEY, Date.now().toString());
  };

  const openPopup = () => {
    setShowPopup(true);
    setSubmitted(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please enter your name and email");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/coupon/claim`, form);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (isAdmin || !config?.enabled) return null;

  const discountText = `${config.discount_percent || 10}% OFF`;

  return (
    <>
      {/* Persistent Side Tab */}
      {showTab && !showPopup && (
        <button
          onClick={openPopup}
          data-testid="promo-side-tab"
          className="fixed right-0 top-1/2 -translate-y-1/2 z-[998] group"
          aria-label="Open promotion"
        >
          <div className="flex items-center bg-[#b51a00] text-white px-3 py-4 rounded-l-lg shadow-lg hover:pr-5 transition-all duration-300 cursor-pointer"
               style={{ writingMode: "vertical-lr", textOrientation: "mixed" }}>
            <Tag className="w-4 h-4 mb-2 rotate-90" />
            <span
              className="text-sm font-black uppercase tracking-wider"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              {config.tab_text || discountText}
            </span>
          </div>
        </button>
      )}

      {/* Popup Modal */}
      {showPopup && (
        <div
          className="fixed inset-0 z-[999] flex items-center justify-center p-4"
          data-testid="promo-popup-overlay"
        >
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300"
            onClick={dismiss}
          />

          <div
            className="relative w-full max-w-md animate-in zoom-in-95 slide-in-from-bottom-4 duration-300"
            data-testid="promo-popup-card"
          >
            <button
              onClick={dismiss}
              data-testid="promo-popup-close"
              className="absolute -top-3 -right-3 z-10 bg-white rounded-full p-1.5 shadow-lg hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>

            <div className="bg-[#1a1a1a] rounded-lg overflow-hidden shadow-2xl noise-texture">
              {/* Header */}
              <div className="bg-[#b51a00] py-4 px-6 text-center">
                <span
                  className="text-5xl font-black text-white uppercase"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  {discountText}
                </span>
              </div>

              {/* Content */}
              <div className="p-6">
                {!submitted ? (
                  <>
                    <h3
                      className="text-2xl font-bold text-white uppercase tracking-tight mb-2 text-center"
                      style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                    >
                      {config.headline || "Limited Time Offer!"}
                    </h3>
                    <p className="text-gray-300 mb-5 text-center text-sm leading-relaxed">
                      {config.description || "Schedule your junk removal service today and save!"}
                    </p>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="space-y-3" data-testid="coupon-form">
                      <div>
                        <Input
                          placeholder="Your Name"
                          value={form.name}
                          onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                          className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                          data-testid="coupon-name-input"
                          required
                        />
                      </div>
                      <div>
                        <Input
                          type="email"
                          placeholder="Email Address"
                          value={form.email}
                          onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                          className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                          data-testid="coupon-email-input"
                          required
                        />
                      </div>
                      <div>
                        <Input
                          type="tel"
                          placeholder="Phone Number (optional)"
                          value={form.phone}
                          onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                          className="bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                          data-testid="coupon-phone-input"
                        />
                      </div>

                      {/* SMS Consent */}
                      <div className="flex items-start gap-2 pt-1">
                        <Checkbox
                          id="sms-consent"
                          checked={form.sms_consent}
                          onCheckedChange={v => setForm(f => ({ ...f, sms_consent: v }))}
                          className="border-white/40 data-[state=checked]:bg-[#b51a00] data-[state=checked]:border-[#b51a00] mt-0.5"
                          data-testid="coupon-sms-checkbox"
                        />
                        <Label htmlFor="sms-consent" className="text-xs text-gray-400 leading-relaxed cursor-pointer">
                          I agree to receive SMS messages from Overhaul Ops. Message & data rates may apply.
                        </Label>
                      </div>

                      {/* Mention text */}
                      <div className="bg-white/10 border border-white/20 rounded-md p-3 mt-3">
                        <p className="text-[#FFD700] text-xs font-medium text-center">
                          {config.mention_text || "Present this coupon after receiving your quote to receive your discount."}
                        </p>
                      </div>

                      {/* Submit */}
                      <Button
                        type="submit"
                        disabled={submitting}
                        data-testid="coupon-submit-btn"
                        className="w-full bg-[#b51a00] hover:bg-[#8a1300] text-white font-bold uppercase tracking-wider rounded-sm py-5 text-base mt-2"
                      >
                        {submitting ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <Mail className="mr-2 w-5 h-5" />
                            Get My 10% Off Coupon
                          </>
                        )}
                      </Button>
                    </form>
                  </>
                ) : (
                  /* Success State */
                  <div className="text-center py-4" data-testid="coupon-success">
                    <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-10 h-10 text-green-400" />
                    </div>
                    <h3
                      className="text-2xl font-bold text-white uppercase tracking-tight mb-3"
                      style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                    >
                      Coupon Sent!
                    </h3>
                    <p className="text-gray-300 mb-2">
                      Check your email for your <strong className="text-[#FFD700]">10% Off Coupon</strong>
                    </p>
                    <p className="text-gray-400 text-sm mb-6">
                      Present this coupon after receiving your quote to receive your discount.
                    </p>
                    <div className="space-y-3">
                      <a href={phone_link} className="block">
                        <Button
                          variant="outline"
                          className="w-full border-2 border-white text-white hover:bg-white hover:text-[#1a1a1a] font-bold uppercase tracking-wider rounded-sm py-4"
                        >
                          <Phone className="mr-2 w-5 h-5" />
                          Call Now — {formatted_phone}
                        </Button>
                      </a>
                      <button
                        onClick={dismiss}
                        className="text-gray-500 hover:text-gray-300 text-sm transition-colors"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
